import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Label } from "@air/primitive-label";
import { VisuallyHidden } from "@air/primitive-visually-hidden";
import { tailwindMerge } from "@air/tailwind-variants";
import classNames from "classnames";
import { useField } from "formik";
import { memo, useCallback, useMemo } from "react";
import { SelectChipOptionContainer } from "../Select/components/SelectChipOptionContainer";
import { orderedSortedFilterFunc, Select } from "../Select/Select";
export const FormikSingleSelect = /*#__PURE__*/ memo((param)=>{
    let { className, "data-testid": dataTestId, disabled = false, isLabelHidden = false, isLoading, isSearchable = true, label, name, onChange, onOptionsClose, onOptionsOpen, options, placeholder, size = "large", maxDropdownHeight = 300 } = param;
    const [field, meta, helpers] = useField(name);
    const hasError = meta.touched && !!meta.error;
    const isExtraLarge = size === "extra-large";
    const selectedOptions = useMemo(()=>{
        const option = options.find((o)=>o.value === field.value);
        return option ? [
            option
        ] : [];
    }, [
        field,
        options
    ]);
    const onSelectionChange = useCallback((param)=>{
        let { chips } = param;
        var _chips_;
        helpers.setValue(((_chips_ = chips[0]) === null || _chips_ === void 0 ? void 0 : _chips_.value) || "");
        onChange === null || onChange === void 0 ? void 0 : onChange();
    }, [
        helpers,
        onChange
    ]);
    const chipRenderer = useCallback((chip)=>{
        return /*#__PURE__*/ _jsx("div", {
            className: classNames("flex justify-between px-2.5", isExtraLarge ? "my-0.5 py-1" : "my-0 py-0.5"),
            children: /*#__PURE__*/ _jsx("div", {
                className: "truncate text-16 text-grey-11",
                children: chip.label
            })
        });
    }, [
        isExtraLarge
    ]);
    const listItemRenderer = useCallback((chip)=>/*#__PURE__*/ _jsx(SelectChipOptionContainer, {
            children: /*#__PURE__*/ _jsx("div", {
                className: classNames("truncate text-grey-11", isExtraLarge ? "py-1.5 text-16" : "py-1 text-14"),
                children: chip.label
            }, chip.value)
        }), [
        isExtraLarge
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        className: tailwindMerge("relative flex min-w-[256px] flex-col items-start justify-center", className),
        "data-testid": dataTestId,
        children: [
            isLabelHidden ? /*#__PURE__*/ _jsx(VisuallyHidden, {
                children: /*#__PURE__*/ _jsx(Label, {
                    htmlFor: name,
                    children: label
                })
            }) : /*#__PURE__*/ _jsx(Label, {
                htmlFor: name,
                className: "mb-1",
                children: label
            }),
            /*#__PURE__*/ _jsx(Select, {
                hasError: !!meta.touched && !!meta.error,
                chipRenderer: chipRenderer,
                listItemRenderer: listItemRenderer,
                closeMenuOnSelect: true,
                disabled: disabled,
                filterFunc: orderedSortedFilterFunc,
                isClearable: false,
                isLoading: isLoading,
                isSearchable: isSearchable,
                isSingleSelect: true,
                onOptionsClose: onOptionsClose,
                onSelectionChange: onSelectionChange,
                options: options,
                placeholder: placeholder,
                renderAsInput: true,
                selectedOptions: selectedOptions,
                showTriangle: true,
                className: "w-full",
                size: size,
                maxDropdownHeight: maxDropdownHeight,
                onOptionsOpen: onOptionsOpen
            }),
            hasError && /*#__PURE__*/ _jsx("div", {
                "data-testid": "".concat(dataTestId, "_error"),
                className: "form-field-error absolute top-full w-full translate-y-0.5 text-12 text-red-9 first-letter:uppercase",
                children: meta.error
            })
        ]
    });
});
FormikSingleSelect.displayName = "FormikSingleSelect";
